import { Component, OnInit, OnDestroy, input, output, signal, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { ToastService } from '../../services/toast.service';
import { IdentifierService } from '../../services/identifier.service';
import { ClientInformation } from '../../models/client-information';
import { Integration } from '../../models/integration';
import { IntegrationIdentifier } from '../../models/integration-identifier';
import { DestinationEndpoint } from '../../models/destination-endpoint';
import { TypeConstantService } from 'src/app/services/type-constant.service';

@Component({
  selector: 'app-add-integration-identifier',
  templateUrl: './add-integration-identifier.component.html',
  styleUrls: ['./add-integration-identifier.component.scss'],
  providers: [ClientIntegrationService, IdentifierService],
  standalone: false,
})
export class AddIntegrationIdentifierComponent implements OnInit, OnDestroy {

  componentDestroyed$ = new Subject<boolean>();
  isInTab = input<boolean>(false);
  inputInAddMode = input<boolean>(false);
  finished = output<string>();
  outputIdentifier = output<IntegrationIdentifier>();
  populatedAdd = input<boolean>(false);
  inputClient = input<ClientInformation>(null);
  inputIntegration = input<Integration>(null);
  inputSelectedIdentifier = input<IntegrationIdentifier>(null);

  inAddMode = signal<boolean>(false);
  client = signal<ClientInformation>(null);
  integration = signal<Integration>(null);
  selectedIdentifier = signal<IntegrationIdentifier>(null);

  hasClient = false;
  clients: ClientInformation[];
  filteredClients: Observable<ClientInformation[]>;
  clientFormControl = new UntypedFormControl();

  hasIntegration = false;
  integrations: Integration[];
  filteredIntegrations: Observable<Integration[]>;
  integrationFormControl = new UntypedFormControl();

  hasDestination = false;
  destination: DestinationEndpoint;
  destinationList: DestinationEndpoint[] = [];
  filteredDestinations: Observable<DestinationEndpoint[]>;
  destinationFormControl = new UntypedFormControl();

  identifiersExist = false; // is selected and edited ids setup
  hasIdentifier = false; // has actual identifier
  identifier: IntegrationIdentifier;
  allIdentifiers: IntegrationIdentifier[];
  editingIdentifier: IntegrationIdentifier;

  private sub: any;
  dateNow: Date;
  checked = true;
  editing = false;
  canFastFetch = true;
  canDataSwap = true;

  savingIdentifier = false;

  pageLoading = true;

  constructor(
    private clientService: ClientIntegrationService,
    private identifierService: IdentifierService,
    private typeConstantService: TypeConstantService,
    private toastService: ToastService,
    private route: ActivatedRoute,
  ) {
    this.destinationList = [];
    effect(() => { this.client.set(this.inputClient() ?? null); });
    effect(() => { this.integration.set(this.inputIntegration() ?? null); });
    effect(() => { this.selectedIdentifier.set(this.inputSelectedIdentifier() ?? null); });
    effect(() => { this.inAddMode.set(this.inputInAddMode() ?? false); });
  }

  ngOnInit(): void {
    this.client.set(this.inputClient() ?? null);
    this.integration.set(this.inputIntegration() ?? null);
    this.selectedIdentifier.set(this.inputSelectedIdentifier() ?? null);
    this.inAddMode.set(this.inputInAddMode() ?? false);

    if (this.client() == null) {
      this.getAllDestinations(false, false);
      this.sub = this.clientService.getAllClients()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (data) => {
            this.clients = data;
            this.filteredClients = this.clientFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterClients(name) : this.clients.slice()),
            );
            this.pageLoading = false;
          },
          error: (_error) => {
            this.pageLoading = false;
          },
        });
      this.hasIntegration = false;
    }
    else {
      this.hasClient = true;

      if (this.integration() == null) {
        this.getIntegrations();
        this.hasIntegration = false;
      }
      else {
        this.hasIntegration = true;

        if (this.selectedIdentifier() == null) {
          this.hasIdentifier = false;
          this.identifiersExist = false;
          this.integrations = [];
          this.integrations.push(this.integration());
          //this.editing = true;

          if (this.inAddMode()) {
            this.editing = false;
            this.getAllDestinations(false, true);
          }
          else {
            this.editing = true;
            this.getAllDestinations(false, false);
          }
        }
        else {
          //this.editingIdentifier = this.selectedIdentifier();
          this.copySelectedIdentifierToEditingIdentifier();
          this.hasIdentifier = true;
          this.identifiersExist = true;
          if (this.inAddMode()) {
            this.editing = false;
            this.getAllDestinations(this.populatedAdd(), !this.populatedAdd());
          }
          else {
            this.editing = true;
            this.getAllDestinations(true, false);
          }

        }

      }
    }
    const date: Date = new Date();
    this.dateNow = date;
  }

  copySelectedIdentifierToEditingIdentifier() {
    const selection = this.selectedIdentifier();
    this.editingIdentifier = null;
    this.editingIdentifier = new IntegrationIdentifier();
    this.editingIdentifier.integrationId = selection.integrationId;
    this.editingIdentifier.destinationId = selection.destinationId;
    this.editingIdentifier.destinationName = selection.destinationName;
    this.editingIdentifier.identifierIdValue = selection.identifierIdValue;
    this.editingIdentifier.identifierNameValue = selection.identifierNameValue;
    this.editingIdentifier.identifierKeyValue = selection.identifierKeyValue;
    this.editingIdentifier.createdDate = selection.createdDate;
    this.editingIdentifier.updatedDate = selection.updatedDate;
    this.editingIdentifier.canFastFetch = selection.canFastFetch;
    this.editingIdentifier.canDataSwap = selection.canDataSwap;
    this.editingIdentifier.isMABase = selection.isMABase;
    this.editingIdentifier.isMABaseCustom = selection.isMABaseCustom;
    this.editingIdentifier.comments = selection.comments ?? '';
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }



  selectClient(c: ClientInformation): void {
    this.pageLoading = true;
    this.client.set(c);
    this.hasClient = true;
    this.getIntegrations();
  }

  editClient() {
    this.client.set(null);
    this.integration.set(null);
    this.hasClient = false;
    this.hasIntegration = false;
    this.hasIdentifier = false;
    this.selectedIdentifier.set(null);
    this.editingIdentifier = null;
    this.hasDestination = false;
    this.destination = null;
    this.clientFormControl.setValue('');
  }

  getClients(): void {
    this.clientService.getAllClients().subscribe((data) => {
      this.clients = data;
      this.filteredClients = this.clientFormControl.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.name),
        map(name => name ? this._filterClients(name) : this.clients.slice()),
      );
    });
  }



  filterDestinationsForAdd() {
    this.identifierService.getIntegrationIdentifiers(this.integration().integrationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(data => {
        if (data != null) {
          if (data.length > 0) {
            this.allIdentifiers = data;
            const destIds = this.allIdentifiers.map(d => d.destinationId);
            if (destIds.length > 0) {
              this.destinationList = this.destinationList.filter(d => destIds.includes(d.destinationEndpointId) == false);
              this.filteredDestinations = this.destinationFormControl.valueChanges.pipe(
                startWith(''),
                map(value => typeof value === 'string' ? value : value.name),
                map(name => name ? this._filterDestinations(name) : this.destinationList.slice()),
              );
            }
          }
          else {
            this.filteredDestinations = this.destinationFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterDestinations(name) : this.destinationList.slice()),
            );
          }
        }
      });
  }

  getAllDestinations(hasDestflag: boolean, filterForAddFlag: boolean): void {
    this.typeConstantService.getAllDestinationEndpoints()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          this.destinationList = data;

          if (hasDestflag) {
            this.destination = this.destinationList.filter(d => d.destinationEndpointId == this.selectedIdentifier().destinationId)[0];
            this.hasDestination = true;
          }
          if (filterForAddFlag) {
            this.filterDestinationsForAdd();
          }
          else {
            this.filteredDestinations = this.destinationFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterDestinations(name) : this.destinationList.slice()),
            );
          }

          this.pageLoading = false;

        },
        error: (_error) => {
          this.pageLoading = false;
        },
      });
  }

  selectIntegration(i: Integration): void {
    this.pageLoading = true;
    this.hasIntegration = true;
    this.integration.set(i);
    this.getAllDestinations(false, false);
  }

  editIntegration() {
    this.integration.set(null);
    this.hasIntegration = false;
    this.hasIdentifier = false;
    this.selectedIdentifier.set(null);
    this.editingIdentifier = null;
    this.hasDestination = false;
    this.destination = null;
    this.integrationFormControl.setValue('');
  }

  getIntegrations(): void {
    if (this.client()) {
      this.clientService.getIntegrationsByClient(this.client().clientDisplayName)
        .subscribe({
          next: (data) => {
            this.integrations = data;
            this.filteredIntegrations = this.integrationFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterIntegrations(name) : this.integrations.slice()),
            );
            this.pageLoading = false;
          },
          error: (_error) => {
            this.pageLoading = false;
          },
        });
    }
    else {
      this.toastService.toastCreate('Select Client.', 'Warning');
    }
  }

  editDestination() {
    this.destination = null;
    this.hasDestination = false;
    this.destinationFormControl.setValue('');
  }


  selectDestination(d: DestinationEndpoint): void {
    this.destination = d;
    this.hasDestination = true;
    this.identifierService.getIntegrationIdentifierByIntegrationAndDestinationId(this.integration().integrationId, d.destinationEndpointId)
      .subscribe({
        next: (i) => {
          if (i != null) {
            //this.editingIdentifier = i; BEE - wondering why we have this commented and why we are setting each field individually?
            this.editingIdentifier = new IntegrationIdentifier();
            this.editingIdentifier.integrationId = i.integrationId;
            this.editingIdentifier.destinationId = i.destinationId;
            this.editingIdentifier.destinationName = i.destinationName;
            this.editingIdentifier.identifierIdValue = i.identifierIdValue;
            this.editingIdentifier.identifierNameValue = i.identifierNameValue;
            this.editingIdentifier.identifierKeyValue = i.identifierKeyValue;
            this.editingIdentifier.createdDate = i.createdDate;
            this.editingIdentifier.updatedDate = i.updatedDate;
            this.editingIdentifier.canFastFetch = i.canFastFetch;
            this.editingIdentifier.canDataSwap = i.canDataSwap;
            this.editingIdentifier.isMABase = i.isMABase;
            this.editingIdentifier.isMABaseCustom = i.isMABaseCustom;
            this.editingIdentifier.comments = i.comments ?? '';
            // this.selectedIdentifier.set(i);
            const newSelected = new IntegrationIdentifier();
            newSelected.integrationId = i.integrationId;
            newSelected.destinationId = i.destinationId;
            newSelected.destinationName = i.destinationName;
            newSelected.identifierIdValue = i.identifierIdValue;
            newSelected.identifierNameValue = i.identifierNameValue;
            newSelected.identifierKeyValue = i.identifierKeyValue;
            newSelected.createdDate = i.createdDate;
            newSelected.updatedDate = i.updatedDate;
            newSelected.canFastFetch = i.canFastFetch;
            newSelected.canDataSwap = i.canDataSwap;
            newSelected.isMABase = i.isMABase;
            newSelected.isMABaseCustom = i.isMABaseCustom;
            newSelected.comments = i.comments ?? '';
            this.selectedIdentifier.set(newSelected);
            this.editing = false;
            this.inAddMode.set(false);
            this.identifiersExist = true;
            this.hasIdentifier = true;
          }
          else {
            const newSelected = new IntegrationIdentifier();
            newSelected.destinationId = this.destination.destinationEndpointId;
            newSelected.destinationName = this.destination.endpointName;
            newSelected.integrationId = this.integration().integrationId;
            newSelected.createdDate = this.dateNow;
            newSelected.updatedDate = this.dateNow;
            this.selectedIdentifier.set(newSelected);
            this.editingIdentifier = new IntegrationIdentifier();
            this.editingIdentifier.destinationId = this.destination.destinationEndpointId;
            this.editingIdentifier.destinationName = this.destination.endpointName;
            this.editingIdentifier.integrationId = this.integration().integrationId;
            this.editingIdentifier.createdDate = this.dateNow;
            this.editingIdentifier.updatedDate = this.dateNow;
            this.inAddMode.set(true);
            this.editing = false;
            this.hasIdentifier = false;
            this.identifiersExist = true;
          }

        },
        error: (_error) => {
          this.hasIdentifier = false;
          this.identifiersExist = false;
        },
      });
  }

  getAllIdentifiers() {
    this.identifierService.getIntegrationIdentifiers(this.integration().integrationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(data => {
        this.allIdentifiers = data;
      });
  }

  insertIntegrationIdentifier(): void {
    this.savingIdentifier = true;
    this.identifierService.addIntegrationIdentifier(this.editingIdentifier).subscribe({
        next: (_value) => {
        if (this.isInTab()) {
          if (this.populatedAdd()) {
            this.finished.emit('generated');
          }
          else {
            this.toastService.toastCreate('Identifier ' + this.editingIdentifier.destinationName + ' Added.', 'Success');
            this.finished.emit('added');
          }
          this.outputIdentifier.emit(this.editingIdentifier);
        }
        else {
          this.toastService.toastCreate('Identifier ' + this.editingIdentifier.destinationName + ' Added.', 'Success');
          this.inAddMode.set(false);
          this.editing = false;
          this.selectDestination(this.destination);
        }
        this.savingIdentifier = false;
      },

        error: (_error)  => {
        if (this.isInTab()) {
          this.finished.emit('error');
        }
        this.savingIdentifier = false;
      },
      });
  }

  updateIntegrationIdentifier(): void {
    this.identifierService.updateIntegrationIdentifier(this.editingIdentifier).subscribe({
        next: (_value) => {
        this.toastService.toastCreate('Identifier ' + this.editingIdentifier.destinationName + ' Updated.', 'Success');
        if (this.isInTab()) {
          this.finished.emit('updated');
          this.outputIdentifier.emit(this.editingIdentifier);
        }
        else {
          this.inAddMode.set(false);
          this.editing = false;
          this.selectDestination(this.destination);
        }
      },
        error: (_error)  => {
        if (this.isInTab()) {
          this.finished.emit('error');
        }
      },
      });
  }

  /* Checkbox Function   */
  changeCanFastFetch(_x: boolean) {
    this.canFastFetch = !this.canFastFetch;
    this.editingIdentifier.canFastFetch = !this.editingIdentifier.canFastFetch;
  }
  changeCanDataSwap(_x: boolean) {
    this.canDataSwap = !this.canDataSwap;
    this.editingIdentifier.canDataSwap = !this.editingIdentifier.canDataSwap;
  }

  /* Angular Material Functions */

  private _filterClients(value: string): ClientInformation[] {
    const filterValue = value.toLowerCase();
    return this.clients.filter(option => option.clientDisplayName.toLowerCase().includes(filterValue));
  }

  private _filterIntegrations(value: string): Integration[] {
    const filterValue = value.toLowerCase();
    return this.integrations.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  private _filterDestinations(value: string): DestinationEndpoint[] {
    const filterValue = value.toLowerCase();
    return this.destinationList.filter(option => option.endpointName.toLowerCase().includes(filterValue));
  }

  displayFnClient(x: ClientInformation): string {
    return x && x.clientDisplayName ? x.clientDisplayName : '';
  }

  displayFnIntegration(x: Integration): string {
    return x && x.name ? x.name : '';
  }

  displayFnDestination(x: DestinationEndpoint): string {
    return x && x.endpointName ? x.endpointName : '';
  }

  toggleEditIdentifier(): void {
    this.editing = true;
  }

  toggleCancelEditIdentifier(): void {
    if (this.isInTab()) {
      this.finished.emit('cancelled');
    }
    this.editing = false;
    this.copySelectedIdentifierToEditingIdentifier();
  }

}
